import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ListChecks, Coins, Receipt, CreditCard, Handshake, Buildings, Package,
  UsersThree, CheckSquare, ClockCounterClockwise, ArrowRight, Sparkle, Graph,
} from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const GROUP_META = {
  tasks:               { label: "Tasks",               icon: ListChecks,         tone: "text-blue-600",    bg: "bg-blue-50/60" },
  finance_entries:     { label: "Finance entries",     icon: Coins,              tone: "text-amber-600",   bg: "bg-amber-50/60" },
  receivables:         { label: "Receivables (AR)",    icon: Receipt,            tone: "text-emerald-600", bg: "bg-emerald-50/60" },
  payables:            { label: "Payables (AP)",       icon: CreditCard,         tone: "text-red-600",     bg: "bg-red-50/60" },
  source_procurement:  { label: "Source procurement",  icon: Handshake,          tone: "text-violet-600",  bg: "bg-violet-50/60" },
  spawned_project:     { label: "Spawned project",     icon: Buildings,          tone: "text-blue-600",    bg: "bg-blue-50/60" },
  spawned_assets:      { label: "Auto-created assets", icon: Package,            tone: "text-slate-600",   bg: "bg-slate-50/70" },
  assets:              { label: "Assets deployed",     icon: Package,            tone: "text-slate-600",   bg: "bg-slate-50/70" },
  team:                { label: "Team",                icon: UsersThree,         tone: "text-emerald-600", bg: "bg-emerald-50/60" },
  approvals:           { label: "Approvals raised",    icon: CheckSquare,        tone: "text-blue-600",    bg: "bg-blue-50/60" },
  salary_requests:     { label: "Payroll history",     icon: Coins,              tone: "text-emerald-600", bg: "bg-emerald-50/60" },
  warnings:            { label: "Warnings",            icon: Sparkle,            tone: "text-amber-600",   bg: "bg-amber-50/60" },
  leaves:              { label: "Leaves",              icon: Sparkle,            tone: "text-blue-600",    bg: "bg-blue-50/60" },
  contracts:           { label: "Contracts",           icon: Sparkle,            tone: "text-slate-600",   bg: "bg-slate-50/70" },
  mobilisations:       { label: "Mobilisations",       icon: Sparkle,            tone: "text-blue-600",    bg: "bg-blue-50/60" },
  rentals:             { label: "Rentals",             icon: Sparkle,            tone: "text-emerald-600", bg: "bg-emerald-50/60" },
  incidents:           { label: "Incidents",           icon: Sparkle,            tone: "text-red-600",     bg: "bg-red-50/60" },
  projects:            { label: "Projects",            icon: Buildings,          tone: "text-blue-600",    bg: "bg-blue-50/60" },
  source:              { label: "Source record",       icon: ArrowRight,         tone: "text-violet-600",  bg: "bg-violet-50/60" },
  audit:               { label: "Audit history",       icon: ClockCounterClockwise, tone: "text-slate-500", bg: "bg-slate-50/70" },
};

/**
 * Reusable cross-module relations panel.
 * Props:
 *   entityType: "project" | "staff" | "asset" | "procurement" | "approval"
 *   entityId: string
 *   compact?: boolean — single-column compact layout (for use inside drawers)
 */
export default function RelationsPanel({ entityType, entityId, compact = false }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!entityType || !entityId) return;
    let active = true;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/relations/${entityType}/${entityId}`);
        if (active) setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (active) setBusy(false);
      }
    })();
    return () => { active = false; };
  }, [entityType, entityId]);

  const goto = (link) => {
    if (!link) return;
    const lk = link.startsWith("/admin") ? link.slice(6) : link;
    navigate(lk);
  };

  if (busy && !data) {
    return (
      <div className="dc-card p-4 font-mono-alt text-xs uppercase tracking-[0.25em] text-slate-400" data-testid="relations-loading">
        Loading relations…
      </div>
    );
  }
  if (!data || data.total === 0) {
    return (
      <div className="dc-card p-4 text-xs text-slate-500 flex items-center gap-2" data-testid="relations-empty">
        <Graph size={14} className="text-slate-400" />
        No related records yet.
      </div>
    );
  }

  const renderGroup = (gkey, items) => {
    const meta = GROUP_META[gkey] || { label: gkey, icon: Sparkle, tone: "text-slate-500", bg: "bg-slate-50/70" };
    const Icon = meta.icon;
    return (
      <div key={gkey} className="dc-card overflow-hidden" data-testid={`relations-group-${gkey}`}>
        <div className={`flex items-center gap-2 border-b border-slate-100 px-4 py-2 ${meta.bg}`}>
          <Icon size={14} className={meta.tone} weight="fill" />
          <h4 className="font-display text-xs font-semibold text-slate-900">{meta.label}</h4>
          <span className="ml-auto font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">{items.length}</span>
        </div>
        <ul className="divide-y divide-slate-100">
          {items.slice(0, compact ? 5 : 12).map((it) => (
            <li key={`${gkey}-${it.id}`}>
              <button
                onClick={() => goto(it.link)}
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs transition hover:bg-slate-50"
                data-testid={`relations-item-${gkey}-${it.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-slate-900">{it.title}</div>
                  {(it.subtitle || it.extra) && (
                    <div className="truncate text-[11px] text-slate-500">
                      {it.subtitle}
                      {it.subtitle && it.extra ? " · " : ""}
                      {it.extra}
                    </div>
                  )}
                </div>
                {it.status && (
                  <span className="font-mono-alt rounded bg-slate-100 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-slate-600">
                    {it.status}
                  </span>
                )}
                <ArrowRight size={10} className="text-slate-400" />
              </button>
            </li>
          ))}
          {items.length > (compact ? 5 : 12) && (
            <li className="px-4 py-1.5 text-[10px] font-mono-alt uppercase tracking-widest text-slate-400">
              +{items.length - (compact ? 5 : 12)} more
            </li>
          )}
        </ul>
      </div>
    );
  };

  const groups = data.groups;
  const ordered = [
    "source", "source_procurement", "spawned_project", "spawned_assets",
    "tasks", "team", "projects", "receivables", "payables", "finance_entries",
    "approvals", "assets", "salary_requests", "warnings", "leaves", "contracts",
    "mobilisations", "rentals", "incidents", "audit",
  ].filter((k) => groups[k]);

  return (
    <div className={compact ? "space-y-3" : "grid grid-cols-1 gap-3 lg:grid-cols-2"} data-testid="relations-panel">
      {ordered.map((gkey) => renderGroup(gkey, groups[gkey]))}
    </div>
  );
}
